import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Flame,
  Shirt,
  Dumbbell,
  Scissors,
  Droplet,
  Trophy,
  ChevronRight,
  Sparkles,
  Crown,
  Heart,
  Calendar,
  Lock,
  Check,
  ArrowRight,
  Camera,
  MessageCircle,
  Image as ImageIcon,
  X,
  Utensils,
  Smile,
} from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import Skeleton from "@/components/Skeleton";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

/**
 * Nova Progress — REAL data only.
 *
 * Never shows fake scores, fake streaks, fake grades, or fake unlocked
 * milestones. Pulls everything from `GET /api/progress/summary` which is
 * computed from the user's actual saved-outfit count, shared-post count,
 * streak, profile completeness, etc.
 *
 * If the user has done nothing yet (`glow_score` is null), the page renders
 * an onboarding checklist instead of a placeholder ring + grades.
 */

const TRACKER_META = {
  style:       { label: "Style",       icon: Shirt,    blurb: "Save outfits, build your wardrobe" },
  fitness:     { label: "Fitness",     icon: Dumbbell, blurb: "Log workouts & weight" },
  hair:        { label: "Hair",        icon: Scissors, blurb: "Track your routine" },
  skin:        { label: "Skin",        icon: Droplet,  blurb: "Track skincare" },
  confidence:  { label: "Confidence",  icon: Heart,    blurb: "Daily coach prompts" },
  consistency: { label: "Consistency", icon: Calendar, blurb: "Show up daily" },
};

const TRACKER_ORDER = ["style", "consistency", "fitness", "hair", "skin", "confidence"];

function ScoreRing({ score, size = 196 }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  const dash = c * (v / 100);
  return (
    <div className="glow-score-ring" style={{ width: size, height: size }} data-testid="progress-score-ring">
      <svg viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="glow-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fff8e7" />
            <stop offset="55%" stopColor="#e8d3b9" />
            <stop offset="100%" stopColor="#c9ad84" />
          </linearGradient>
        </defs>
        <circle className="track" cx={size / 2} cy={size / 2} r={r} />
        <circle
          className="progress"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <div className="glow-score-num">
        <span className="num numeric" data-testid="progress-score-number">{Math.round(v)}</span>
        <span className="label">Nova Score</span>
      </div>
    </div>
  );
}

function OnboardingChecklist({ steps, onStep }) {
  return (
    <div className="onboarding-list" data-testid="progress-onboarding">
      {steps.map((s) => (
        <button
          key={s.id}
          type="button"
          className={`onboarding-row ${s.done ? "done" : ""}`}
          onClick={() => !s.done && s.route && onStep(s.route)}
          disabled={s.done}
          data-testid={`progress-onboarding-${s.id}`}
        >
          <span className={`onboarding-check ${s.done ? "done" : ""}`}>
            {s.done ? <Check size={14} strokeWidth={2.4}/> : null}
          </span>
          <span className="onboarding-label">
            <span className={s.done ? "onboarding-strike" : ""}>{s.label}</span>
            {s.done && s.value_when_done && (
              <span className="onboarding-sub"> · {s.value_when_done}</span>
            )}
          </span>
          {!s.done && <ArrowRight size={15} strokeWidth={1.7} className="onboarding-arrow"/>}
        </button>
      ))}
    </div>
  );
}

export default function ProgressScreen() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [weekly, setWeekly] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [wrapped, setWrapped] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showWrapped, setShowWrapped] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [pickedIdxs, setPickedIdxs] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/progress/summary").then((r) => setSummary(r.data)).catch((err) => setError(err?.response?.data?.detail || "Couldn't load progress."));
    api.get("/progress/weekly").then((r) => setWeekly(r.data)).catch(() => {});
    api.get("/progress/photos").then((r) => setPhotos(r.data.photos || [])).catch(() => setPhotos([]));
    api.get("/progress/wrapped").then((r) => setWrapped(r.data)).catch(() => {});
  }, []);

  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/image\//.test(f.type)) return;
    setUploading(true);
    try {
      // Resize client-side for speed
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const img = new Image();
          img.onload = () => {
            const max = 1280;
            const s = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.round(img.width * s), h = Math.round(img.height * s);
            const c = document.createElement("canvas");
            c.width = w; c.height = h;
            c.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(c.toDataURL("image/jpeg", 0.85));
          };
          img.onerror = reject;
          img.src = r.result;
        };
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      const b64 = dataUrl.split(",", 2)[1];
      const { data: doc } = await api.post("/progress/photos", { image_base64: b64, image_mime: "image/jpeg", category: "full_body" });
      setPhotos((p) => [doc, ...(p || [])]);
      try { track("feature_completed", { feature: "progress", sub_action: "photo_added" }); } catch { /* ignore */ }
      // Re-fetch summary so streak/score updates if this counts as today's activity
      api.get("/progress/summary").then((r) => setSummary(r.data)).catch(() => {});
    } catch { /* upload failed silently — user can retry */ } finally { setUploading(false); }
  };

  const togglePick = (idx) => {
    setPickedIdxs((p) => {
      if (p.includes(idx)) return p.filter((x) => x !== idx);
      if (p.length >= 2) return [p[1], idx];
      return [...p, idx];
    });
  };

  const empty = useMemo(
    () => summary && summary.glow_score == null,
    [summary]
  );
  const doneCount = useMemo(
    () => summary?.onboarding?.filter((s) => s.done).length || 0,
    [summary]
  );

  if (!summary && !error) {
    return (
      <div className="screen with-nav fade-in" data-testid="progress-screen-loading">
        <div className="top-bar">
          <div className="logo-mark">NOVA</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 24 }}>
          <Skeleton w="50%" h={14} />
          <Skeleton w="80%" h={36} />
          <Skeleton w="100%" h={260} r={28} />
          <Skeleton w="100%" h={120} r={20} />
          <Skeleton w="100%" h={120} r={20} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="screen with-nav" data-testid="progress-screen-error">
        <div className="top-bar"><div className="logo-mark">NOVA</div></div>
        <div className="eyebrow" style={{ color: "var(--danger)" }}>SOMETHING WENT WRONG</div>
        <p className="body" style={{ marginTop: 10, color: "#fff" }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="screen with-nav fade-in" data-testid="progress-screen">
      <div className="top-bar">
        <div className="logo-mark" data-testid="progress-logo">NOVA</div>
        {summary.is_premium ? (
          <span className="premium-pill" data-testid="progress-premium-pill">
            <Crown size={11} strokeWidth={2.2}/> Premium
          </span>
        ) : (
          <button
            type="button"
            className="premium-pill"
            onClick={() => navigate("/premium")}
            data-testid="progress-upgrade-pill"
            style={{ background: "transparent", cursor: "pointer" }}
          >
            <Crown size={11} strokeWidth={2.2}/> Upgrade
          </button>
        )}
      </div>

      <div className="eyebrow" style={{ marginBottom: 10 }}>YOUR PROGRESS</div>
      <h2 className="h1" style={{ fontSize: 32, marginBottom: 22, lineHeight: 1.08 }}>
        Every day is a chance<br/>
        to <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>become better</em>.
      </h2>

      {/* HERO — either onboarding (if user has nothing yet) or the journey */}
      {empty ? (
        <div className="glow-empty-hero" data-testid="progress-empty-hero">
          <div className="glow-empty-badge">
            <Sparkles size={14} strokeWidth={2}/> Start your journey
          </div>
          <h3 className="h2" style={{ fontSize: 22, marginTop: 14, color: "#fff", lineHeight: 1.2 }}>
            Your Glow Journey starts with one action.
          </h3>
          <p className="body-sm" style={{ marginTop: 8, color: "rgba(255,255,255,0.6)" }}>
            No scoring, no judgement — just milestones to celebrate as you go.
          </p>
          <div className="glow-empty-progress">
            <div className="glow-empty-progress-fill" style={{ width: `${(doneCount / summary.onboarding.length) * 100}%` }} />
          </div>
        </div>
      ) : (
        <div className="glow-score-hero glow-journey-hero" data-testid="progress-journey-hero">
          {/* Glow Journey — milestone-first hero, no /100 score, no counts */}
          {(() => {
            const ms = summary.milestones || [];
            const unlocked = ms.filter(m => m.unlocked);
            const next = ms.find(m => !m.unlocked);
            return (
              <>
                <div className="tiny" style={{ letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--gold)" }}>
                  GLOW JOURNEY
                </div>
                <div className="serif" style={{
                  fontFamily: "'Fraunces', serif", fontSize: 26, lineHeight: 1.15,
                  letterSpacing: "-0.01em", color: "var(--text)", marginTop: 10, fontWeight: 400,
                }} data-testid="progress-journey-headline">
                  {unlocked.length === 0
                    ? <>Your journey<br/><em style={{ fontStyle: "italic", fontWeight: 300 }}>starts today</em>.</>
                    : next
                    ? <>You&apos;ve unlocked<br/><em style={{ fontStyle: "italic", fontWeight: 300 }}>{unlocked[unlocked.length - 1].name}</em>.</>
                    : <>Every milestone<br/><em style={{ fontStyle: "italic", fontWeight: 300 }}>unlocked</em>.</>
                  }
                </div>
                {next && (
                  <div className="body-sm" style={{ marginTop: 14, color: "var(--text-dim)", lineHeight: 1.5 }} data-testid="progress-journey-next">
                    Next up · <strong style={{ color: "var(--text)" }}>{next.name}</strong> — {next.sub}
                  </div>
                )}
              </>
            );
          })()}
          <div className={`glow-streak-pill ${summary.streak_days > 0 ? "" : "muted"}`} data-testid="progress-streak-pill" style={{ marginTop: 18 }}>
            <Flame size={14} strokeWidth={2}/>
            {summary.streak_days > 0
              ? `${summary.streak_days}-day streak`
              : "Start your streak today."}
          </div>
        </div>
      )}

      {/* Category achievements — icon + milestone unlocks instead of /100 scores. */}
      {summary.dimensions && (
        <div className="outfit-section" data-testid="progress-dimensions">
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
            <div className="eyebrow">YOUR PROGRESS BY CATEGORY</div>
            {summary.improve_first && (
              <div className="tiny gold-text" style={{ letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>
                BUILD YOUR BEST BODY
              </div>
            )}
          </div>
          <div className="cat-grid" data-testid="progress-cat-grid">
            {(() => {
              // Icon + relevant-milestone mapping per dimension.
              // Achievements are computed from the existing milestones array,
              // so they stay in sync with the celebration system automatically.
              const ms = summary.milestones || [];
              const has = (id) => ms.find((m) => m.id === id && m.unlocked);
              const ICONS = {
                style:       Shirt,
                grooming:    Sparkles,
                skincare:    Droplet,
                fitness:     Dumbbell,
                nutrition:   Utensils,
                confidence:  Smile,
                consistency: Flame,
              };
              const MAP = {
                style:       ["first_save", "ten_fits"],
                grooming:    ["profile_set"],
                skincare:    [],                                   // milestone added when skin analysis is logged in future iteration
                fitness:     [],
                nutrition:   ["first_scan"],
                confidence:  [],
                consistency: ["three_streak", "seven_streak", "first_photo"],
              };
              const isStarted = {
                style:       (summary.saved_outfits || 0) > 0,
                grooming:    summary.has_appearance,
                skincare:    summary.has_appearance,
                fitness:     false,
                nutrition:   (summary.nutrition_scans || 0) > 0,
                confidence:  (summary.coach_messages || 0) > 0,
                consistency: (summary.streak_days || 0) > 0 || (summary.total_photos || 0) > 0,
              };
              return Object.entries(summary.dimensions).map(([id, d]) => {
                const Icon = ICONS[id] || Sparkles;
                const relevant = MAP[id] || [];
                const unlockedCount = relevant.filter((mid) => has(mid)).length;
                const started = isStarted[id];
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => {
                      // Tapping a category routes to the relevant hub.
                      const routes = {
                        style: "/style", grooming: "/glow-up/analyze", skincare: "/improve/skincare",
                        fitness: "/improve/fitness", nutrition: "/nutrition", confidence: "/coach", consistency: "/progress",
                      };
                      navigate(routes[id] || "/progress");
                    }}
                    className={"cat-card" + (started ? " is-active" : "")}
                    data-testid={`progress-cat-${id}`}
                  >
                    <div className="cat-card-icon">
                      <Icon size={20} strokeWidth={1.7} />
                    </div>
                    <div className="cat-card-label">{d.label}</div>
                    <div className="cat-card-meta" data-testid={`progress-cat-meta-${id}`}>
                      {started ? (
                        relevant.length > 0 && unlockedCount > 0
                          ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Check size={11} strokeWidth={2.4} /> Underway</span>
                          : "Started"
                      ) : "Tap to begin"}
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Nutrition CTA */}
      <button
        type="button"
        className="coach-cta-card"
        onClick={() => navigate("/nutrition")}
        data-testid="progress-nutrition-cta"
        style={{ marginTop: 14, background: "linear-gradient(180deg, rgba(232,211,185,0.06) 0%, rgba(232,211,185,0.02) 100%)" }}
      >
        <div className="coach-cta-icon" style={{ background: "rgba(232,211,185,0.18)", color: "var(--gold)" }}>
          <Utensils size={20} strokeWidth={1.7}/>
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div className="h3" style={{ color: "#fff" }}>Calculate your nutrition</div>
          <div className="tiny" style={{ color: "rgba(255,255,255,0.55)", marginTop: 4, letterSpacing: "0.06em" }}>
            Real calories · protein · water · meal ideas
          </div>
        </div>
        <ChevronRight size={18} strokeWidth={1.7} color="rgba(255,255,255,0.5)"/>
      </button>

      {/* Onboarding checklist — shown until all 5 steps are done */}
      {!summary.all_onboarding_done && (
        <div className="outfit-section" data-testid="progress-onboarding-section">
          <div className="eyebrow" style={{ marginBottom: 12 }}>
            {empty ? "DO THIS FIRST" : `FINISH SETUP · ${doneCount}/${summary.onboarding.length}`}
          </div>
          <OnboardingChecklist
            steps={summary.onboarding}
            onStep={(route) => navigate(route)}
          />
        </div>
      )}

      {/* Sub-trackers — REAL values where we have them, locked otherwise */}
      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 14 }}>TRACK YOUR GLOW</div>
        <div className="glow-sub-grid" data-testid="progress-sub-grid">
          {TRACKER_ORDER.map((id) => {
            const meta = TRACKER_META[id];
            const t = summary.trackers[id] || {};
            const Icon = meta.icon;
            const isLocked = t.real_data === false;
            return (
              <button
                key={id}
                type="button"
                className={`glow-sub-card ${isLocked ? "locked" : ""}`}
                onClick={() => t.route && navigate(t.route)}
                disabled={isLocked && !t.route}
                data-testid={`progress-sub-${id}`}
              >
                <div className="glow-sub-card-icon">
                  {isLocked && t.premium ? <Lock size={14} strokeWidth={1.8}/> : <Icon size={16} strokeWidth={1.8}/>}
                </div>
                <div className="glow-sub-card-label">{meta.label}</div>
                {t.value ? (
                  <div className="glow-sub-card-value">{t.value}</div>
                ) : (
                  <div className="glow-sub-card-locked">{meta.blurb}</div>
                )}
                <div className="glow-sub-card-trend">{t.trend}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* This week — calendar dots row, today highlighted only. No bars, no numbers. */}
      {weekly && (
        <div className="outfit-section" data-testid="progress-weekly">
          <div className="eyebrow" style={{ marginBottom: 12 }}>THIS WEEK</div>
          <div className="week-dots" data-testid="progress-week-dots">
            {weekly.days.map((d) => (
              <div
                key={d.date}
                className={`week-dot-col ${d.is_today ? "is-today" : ""} ${d.active ? "is-active" : ""}`}
                data-testid={`progress-week-${d.day_short}`}
              >
                <div className="week-dot-day">{d.day_short}</div>
                <div className="week-dot" aria-label={d.is_today ? "Today" : d.active ? "Active" : "No activity"}>
                  {d.is_today
                    ? <Sparkles size={11} strokeWidth={2} />
                    : d.active
                    ? <Check size={11} strokeWidth={2.5} />
                    : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress photos */}
      <div className="outfit-section" data-testid="progress-photos-section">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
          <div className="eyebrow">PROGRESS PHOTOS</div>
          {photos?.length >= 2 && !comparing && (
            <button
              type="button"
              className="tiny gold-text"
              onClick={() => { setComparing(true); setPickedIdxs([]); }}
              data-testid="progress-compare-btn"
              style={{ background: "transparent", border: "none", cursor: "pointer", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600 }}
            >
              Compare ↔
            </button>
          )}
        </div>

        {comparing ? (
          <div data-testid="progress-compare-area">
            <p className="body-sm" style={{ color: "rgba(255,255,255,0.55)", marginBottom: 12 }}>
              Pick TWO photos to slide between them.
            </p>
            <div className="photo-grid">
              {photos.map((p, i) => {
                const picked = pickedIdxs.includes(i);
                const order = pickedIdxs.indexOf(i);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`photo-tile ${picked ? "picked" : ""}`}
                    onClick={() => togglePick(i)}
                    data-testid={`progress-photo-tile-${i}`}
                  >
                    <img src={`data:${p.image_mime};base64,${p.image_base64}`} alt={p.taken_date}/>
                    {picked && <span className="photo-tile-badge">{order === 0 ? "B" : "A"}</span>}
                  </button>
                );
              })}
            </div>
            {pickedIdxs.length === 2 && (
              <div style={{ marginTop: 14 }}>
                <BeforeAfterSlider
                  beforeSrc={`data:${photos[pickedIdxs[0]].image_mime};base64,${photos[pickedIdxs[0]].image_base64}`}
                  afterSrc={`data:${photos[pickedIdxs[1]].image_mime};base64,${photos[pickedIdxs[1]].image_base64}`}
                  beforeLabel={photos[pickedIdxs[0]].taken_date}
                  afterLabel={photos[pickedIdxs[1]].taken_date}
                  testId="progress-before-after"
                />
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button className="btn btn-outline" onClick={() => { setComparing(false); setPickedIdxs([]); }} data-testid="progress-compare-done">Done</button>
            </div>
          </div>
        ) : photos === null ? (
          <Skeleton w="100%" h={120} r={18} />
        ) : photos.length === 0 ? (
          <div className="photo-empty" data-testid="progress-photos-empty">
            <div className="photo-empty-icon"><Camera size={18} strokeWidth={1.8}/></div>
            <div className="body-sm" style={{ color: "#fff", marginBottom: 4 }}>Take your first progress photo</div>
            <div className="tiny" style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.10em" }}>Same lighting, same pose, every day</div>
            <button
              type="button"
              className="btn btn-gold"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="progress-photo-upload-btn"
              style={{ marginTop: 14 }}
            >
              {uploading ? "Uploading…" : (<><Camera size={14}/> Snap your start</>)}
            </button>
          </div>
        ) : (
          <>
            <div className="photo-grid" data-testid="progress-photo-grid">
              <button
                type="button"
                className="photo-add"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="progress-photo-add"
              >
                {uploading ? <div className="spinner"/> : <Camera size={18} strokeWidth={1.8}/>}
                <span className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase" }}>Add today</span>
              </button>
              {photos.map((p, i) => (
                <div key={p.id} className="photo-tile" data-testid={`progress-photo-${i}`}>
                  <img src={`data:${p.image_mime};base64,${p.image_base64}`} alt={p.taken_date}/>
                  <span className="photo-tile-date">{p.taken_date.slice(5)}</span>
                </div>
              ))}
            </div>
            {photos.length >= 2 && (
              <p className="tiny" style={{ color: "rgba(255,255,255,0.45)", marginTop: 10, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                {photos.length} photos · tap Compare to slide between them
              </p>
            )}
          </>
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onPhotoPick} data-testid="progress-photo-input"/>
      </div>

      {/* Milestones — REAL unlocked state from backend */}
      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 14 }}>MILESTONES</div>
        <div className="glow-milestone-row" data-testid="progress-milestones">
          {summary.milestones.map((m) => (
            <div
              key={m.id}
              className={`glow-milestone ${m.unlocked ? "" : "locked"}${m.id === "first_skin" ? " clickable" : ""}`}
              data-testid={`progress-milestone-${m.id}`}
              onClick={m.id === "first_skin" ? () => navigate("/improve/skincare") : undefined}
              role={m.id === "first_skin" ? "button" : undefined}
              style={m.id === "first_skin" ? { cursor: "pointer" } : undefined}
            >
              <div className="glow-milestone-icon">
                {m.id === "first_skin"
                  ? <Smile size={16} strokeWidth={1.8}/>
                  : (m.unlocked ? <Trophy size={16} strokeWidth={1.8}/> : <Lock size={14} strokeWidth={1.8}/>)}
              </div>
              <div className="glow-milestone-name">{m.name}</div>
              <div className="glow-milestone-sub">{m.unlocked ? "Unlocked" : m.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Nova Coach — FREE for launch */}
      <button
        type="button"
        className="coach-cta-card"
        onClick={() => navigate("/coach")}
        data-testid="progress-coach-cta"
      >
        <div className="coach-cta-icon"><MessageCircle size={20} strokeWidth={1.7}/></div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div className="h3" style={{ color: "var(--gold)" }}>Talk to your Nova Coach</div>
          <div className="tiny" style={{ color: "rgba(255,255,255,0.65)", marginTop: 4, letterSpacing: "0.06em" }}>
            Fashion · hair · cologne · skin · fitness · confidence · FREE for launch
          </div>
        </div>
        <ChevronRight size={18} strokeWidth={1.7} color="var(--gold)"/>
      </button>

      {/* Nova Wrapped — icon + label only, no numerical breakdown */}
      {wrapped?.has_data && (
        <button
          type="button"
          className="wrapped-cta"
          onClick={() => setShowWrapped(true)}
          data-testid="progress-wrapped-cta"
        >
          <span className="wrapped-emoji-mark"><Sparkles size={16} strokeWidth={1.8}/></span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <div style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>Your Nova Wrapped</div>
            <div className="tiny" style={{ color: "rgba(255,255,255,0.6)", marginTop: 3 }}>
              See your journey so far
            </div>
          </span>
          <ChevronRight size={18} strokeWidth={1.7} color="rgba(255,255,255,0.5)"/>
        </button>
      )}

      {showWrapped && wrapped && (
        <WrappedModal data={wrapped} onClose={() => setShowWrapped(false)} />
      )}

      {/* Daily nudge — only when fully onboarded, encourage daily return */}
      {summary.all_onboarding_done && (
        <div className="outfit-section" data-testid="progress-daily-nudge">
          <div className="eyebrow" style={{ marginBottom: 12 }}>TODAY&apos;S NUDGE</div>
          <button
            type="button"
            className="card"
            onClick={() => navigate("/upload")}
            data-testid="progress-nudge-btn"
            style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", width: "100%", textAlign: "left" }}
          >
            <div className="tile-icon"><Sparkles size={18} strokeWidth={1.7}/></div>
            <div style={{ flex: 1 }}>
              <div className="h3" style={{ color: "#fff" }}>Generate today&apos;s 4 fits</div>
              <div className="tiny" style={{ color: "rgba(255,255,255,0.5)", marginTop: 4 }}>
                Keep the streak alive · 30 seconds
              </div>
            </div>
            <ChevronRight size={18} strokeWidth={1.5} color="rgba(255,255,255,0.4)"/>
          </button>
        </div>
      )}
    </div>
  );
}

function WrappedModal({ data, onClose }) {
  return (
    <div className="wrapped-overlay" onClick={onClose} data-testid="wrapped-modal">
      <div className="wrapped-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="icon-btn wrapped-close" onClick={onClose} data-testid="wrapped-close">
          <X size={16}/>
        </button>
        <div className="eyebrow" style={{ color: "var(--gold)", letterSpacing: "0.30em" }}>NOVA · WRAPPED</div>
        <h2 className="h1" style={{ fontSize: 32, lineHeight: 1.1, marginTop: 16 }}>
          Your<br/>
          <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>year of glow</em>.
        </h2>
        <div className="wrapped-stats">
          <div className="wrapped-stat">
            <div className="wrapped-stat-num numeric">{data.outfits_generated}</div>
            <div className="wrapped-stat-label">Outfits generated</div>
          </div>
          <div className="wrapped-stat">
            <div className="wrapped-stat-num numeric">{data.saved_outfits}</div>
            <div className="wrapped-stat-label">Fits saved</div>
          </div>
          <div className="wrapped-stat">
            <div className="wrapped-stat-num numeric">{data.shared_posts}</div>
            <div className="wrapped-stat-label">Posts shared</div>
          </div>
          <div className="wrapped-stat">
            <div className="wrapped-stat-num numeric">{data.progress_photos}</div>
            <div className="wrapped-stat-label">Progress photos</div>
          </div>
          <div className="wrapped-stat">
            <div className="wrapped-stat-num numeric">{data.longest_streak}</div>
            <div className="wrapped-stat-label">Longest streak</div>
          </div>
          {data.top_style && (
            <div className="wrapped-stat">
              <div className="wrapped-stat-num" style={{ fontSize: 22, textTransform: "capitalize" }}>{data.top_style}</div>
              <div className="wrapped-stat-label">Your signature</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
